import {
  ICreativeAudio,
  ICreativeImage,
  ICreativeRedirect,
} from 'features/direct/creative/graphql/queries';
import { ITargetingDefinitionV2 } from 'features/targetingV2/types/targeting';
import { OptionType } from 'interfaces';
import {
  AdStatusLegacy,
  CampaignStatus,
  TargetingVersion,
  Territory,
} from 'interfaces/generated.types';
import memoizeOne from 'memoize-one';
import dateUtils, { TimeZones } from 'utils/date';
import {
  ITargetingGroup,
  ITargetingMasterTemplate,
} from 'utils/targetingDefinitions';

export interface IAdValues {
  campaignCpm: string;
  duration: string;
  endDate: Date | null;
  endDateIsInherited?: boolean;
  isEndDateInheritable?: boolean;
  frequencyCapCount: number | string;
  frequencyCapTimeUnit: string;
  frequencyCapValue: number | string;
  id: string;
  altId?: number;
  name: string;
  sequence: { id: string }[];
  startDate: Date | null;
  startDateIsInherited?: boolean;
  isStartDateInheritable?: boolean;
  weighting: number | string;
  audioCreatives: ICreativeAudio[];
  creativeRedirect: ICreativeRedirect;
  campaignName: string;
  campaignId: string;
  campaignStartDate: Date | null;
  imageCreatives: ICreativeImage[];
  targetingGroups: ITargetingGroup[];
  targetingMasterTemplates: ITargetingMasterTemplate[];
  targetingGeneralGroup: ITargetingGroup | null;
  tags: OptionType[];
  tagsAreInherited: boolean;
  inheritedTags: OptionType[];
  journey: string;
  status?: AdStatusLegacy;
  dailyCap: string;
  adExclusive: boolean;
  territory: Territory;
  targetingVersion: TargetingVersion;
  targetingDefinitionV2: ITargetingDefinitionV2 | null;
}

export interface IAdStatusValues {
  value: AdStatusLegacy;
  label: string;
  isDisabled?: (value: AdStatusLegacy) => boolean;
}

export const adStatusValues: IAdStatusValues[] = [
  {
    value: AdStatusLegacy.Disabled,
    label: 'Disabled',
  },
  {
    value: AdStatusLegacy.Draft,
    label: 'Draft',
    isDisabled: (value: AdStatusLegacy) =>
      [
        AdStatusLegacy.Ready,
        AdStatusLegacy.Live,
        AdStatusLegacy.Paused,
        AdStatusLegacy.Completed,
      ].includes(value),
  },
  {
    value: AdStatusLegacy.Ready,
    label: 'Ready',
    isDisabled: (value: AdStatusLegacy) =>
      [
        AdStatusLegacy.Draft,
        AdStatusLegacy.Live,
        AdStatusLegacy.Paused,
        AdStatusLegacy.Disabled,
        AdStatusLegacy.Completed,
      ].includes(value),
  },
  {
    value: AdStatusLegacy.Live,
    label: 'Live',
    isDisabled: (value: AdStatusLegacy) =>
      [
        AdStatusLegacy.Draft,
        AdStatusLegacy.Ready,
        AdStatusLegacy.Disabled,
        AdStatusLegacy.Completed,
      ].includes(value),
  },
  {
    value: AdStatusLegacy.Paused,
    label: 'Paused',
    isDisabled: (value: AdStatusLegacy) =>
      [
        AdStatusLegacy.Draft,
        AdStatusLegacy.Ready,
        AdStatusLegacy.Disabled,
        AdStatusLegacy.Completed,
      ].includes(value),
  },
  {
    value: AdStatusLegacy.Completed,
    label: 'Completed',
    isDisabled: () => true,
  },
];

export const getAdStatusData = memoizeOne(
  (
    values: IAdValues,
    campaignStatus: CampaignStatus,
    campaignTimeZone: TimeZones
  ) => {
    let statusData = [
      {
        status: AdStatusLegacy.Draft,
        required: [
          ...(!values.name ? ["Complete 'Name'"] : []),
          ...(!values.campaignName ? ['Complete Parent Campaign'] : []),
        ],
      },
      {
        status: AdStatusLegacy.Ready,
        required: [
          ...(!values.name ? ['Waiting for Name'] : []),
          ...(!(
            campaignStatus === CampaignStatus.Ready ||
            campaignStatus === CampaignStatus.Live
          )
            ? ['Waiting for Parent Campaign to be in READY or LIVE state']
            : []),
          ...(!values.startDate ? ['Waiting for Start Date'] : []),
          ...(!values.duration ? ['Waiting for Ad Duration'] : []),
          ...(values.targetingVersion === TargetingVersion.TargetingV1 &&
          !(
            values.targetingGroups.length ||
            values.targetingMasterTemplates.length
          )
            ? ['Waiting for at least one Targeting Group']
            : []),
        ],
      },
      {
        status:
          values.status === AdStatusLegacy.Paused
            ? AdStatusLegacy.Paused
            : AdStatusLegacy.Live,
        required: [
          ...(campaignStatus !== CampaignStatus.Live
            ? ['Waiting for Parent Campaign to be in LIVE state']
            : []),
          ...((values.startDate &&
            dateUtils.isDateInTheFuture(values.startDate, campaignTimeZone)) ||
          !values.startDate
            ? ['Waiting for Start Date']
            : []),
          ...(!(values.creativeRedirect.url || values.audioCreatives.length)
            ? ['Waiting for either a Creative Audio or Creative Redirect']
            : []),
        ],
      },
      {
        status: AdStatusLegacy.Completed,
        required: ['Waiting for End Date'],
      },
    ];

    if (values.status === AdStatusLegacy.Disabled) {
      statusData = [
        {
          status: AdStatusLegacy.Disabled,
          required: [],
        },
        ...statusData,
      ];
    }
    return statusData;
  }
);

export const adDetailsFields = [
  'name',
  'weighting',
  'dailyCap',
  'startDate',
  'endDate',
  'frequencyCapCount',
  'frequencyCapTimeUnit',
  'frequencyCapValue',
  'duration',
  'territory',
];
